import { useEffect, useState } from "react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useSearchMessages = ({
  searchParam,
  pageNumber,
  queueIds,
  tags,
  users,
  mediaFilter,
  refreshToken = 0,
  enabled = true,
}) => {
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [messages, setMessages] = useState([]);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (pageNumber === 1) {
      setMessages([]);
      setHasMore(false);
      setCount(0);
    }
  }, [searchParam, pageNumber, queueIds, tags, users, mediaFilter, refreshToken]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return () => {};
    }

    setLoading(true);
    const shouldDebounceSearch = Boolean(searchParam && String(searchParam).trim().length > 0);
    const debounceDelay = shouldDebounceSearch ? 150 : 0;
    const controller = new AbortController();
    let didCancel = false;

    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        const startTime = performance.now();
        const logEnabled = process.env.NODE_ENV === "development";

        try {
          const { data } = await api.get("/messages/search", {
            params: {
              searchParam,
              pageNumber,
              queueIds,
              tags,
              users,
              mediaFilter,
            },
            signal: controller.signal,
          });

          if (didCancel) {
            return;
          }

          setMessages(data.messages || []);
          setHasMore(Boolean(data.hasMore));
          setCount(data.count || 0);
          setLoading(false);

          if (logEnabled) {
            const duration = performance.now() - startTime;
            // eslint-disable-next-line no-console
            console.debug("[TIMING] useSearchMessages", {
              durationMs: Number(duration.toFixed(2)),
              pageNumber,
              count: data.count,
              hasMore: data.hasMore,
              searchParam,
              mediaFilter,
            });
          }
        } catch (err) {
          if (didCancel || err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
            return;
          }

          setLoading(false);

          if (logEnabled) {
            const duration = performance.now() - startTime;
            // eslint-disable-next-line no-console
            console.debug("[TIMING] useSearchMessages.error", {
              durationMs: Number(duration.toFixed(2)),
              pageNumber,
              searchParam,
              mediaFilter,
              message: err?.message,
            });
          }

          toastError(err);
        }
      };

      fetchMessages();
    }, debounceDelay);

    return () => {
      didCancel = true;
      controller.abort();
      clearTimeout(delayDebounceFn);
    };
  }, [searchParam, pageNumber, queueIds, tags, users, mediaFilter, refreshToken, enabled]);

  return { messages, loading, hasMore, count };
};

export default useSearchMessages;
